import { Component, OnInit, Input, OnDestroy, ChangeDetectorRef, inject, Optional, SkipSelf } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatAngularModule } from '../../modules/mat-angular.module';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { BarLineComboChartComponent } from '../../charts/bar-line-combo-chart/bar-line-combo-chart.component';
import { CitationCardComponent } from '../../components/citation-card/citation-card.component';
import { PromptTrackingService, PromptTrackingData, CompositeScore, PromptTrackingTaskStatus, PromptTrackingTaskResponse } from '../../../services/prompt-tracking.service';
import { PromptResearchService, PromptResearch } from '../../../services/prompt-research.service';
import { PromptDetailDialogComponent } from '../../../dialogs/prompt-detail-dialog/prompt-detail-dialog.component';
import {
  PromptTrackingSourceDialogComponent,
  PromptTrackingSourceDialogData,
  PromptTrackingSourceResult,
} from '../../../dialogs/prompt-tracking-source-dialog/prompt-tracking-source-dialog.component';
import { GenericTemplateCommonService } from '../../components/template-ai-common/services/generic-template-common.service';
import { GenericCommonTabsComponent } from '../../components/template-ai-common/generic-common-tabs/generic-common-tabs.component';
import { TaskProgressWebSocketService } from '../../../services/task-progress-websocket.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

/** Maximum number of branded prompts to display (matches backend BRANDED_PROMPT_TRACKING_TEMPLATES). */
const MAX_BRANDED_PROMPTS_DISPLAY = 15;

@Component({
  selector: 'app-generic-prompt-tracking',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatAngularModule,
    MatTabsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    NgxChartsModule,
    BarLineComboChartComponent,
  ],
  templateUrl: './generic-prompt-tracking.component.html',
  styleUrl: './generic-prompt-tracking.component.scss'
})
export class GenericPromptTrackingComponent implements OnInit, OnDestroy {
  @Input() selectedEntities: any[] = [];

  private promptTrackingService = inject(PromptTrackingService);
  private templateCommonService = inject(GenericTemplateCommonService);
  private taskProgressWs = inject(TaskProgressWebSocketService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  cdr = inject(ChangeDetectorRef);
  
  // Inject parent component for tab navigation
  constructor(@Optional() @SkipSelf() private parentTabs?: GenericCommonTabsComponent) {}

  // Current project ID
  currentProjectId: string | null = null;
  
  // Composite Score
  compositeScore: CompositeScore | null = null;
  
  // Chart data
  selectedMetricTab: 'mentions' | 'citations' | 'impressions' | 'ai_share_of_voice' = 'mentions';
  chartData: any[] = [];
  lineChart: any[] = [];
  
  // Chart properties
  view: [number, number] = [600, 400];
  comboBarScheme: any = {
    name: 'singleLightBlue',
    selectable: true,
    group: 'ordinal',
    domain: ['#1976d2']
  };
  lineChartScheme: any = {
    name: 'coolthree',
    selectable: true,
    group: 'ordinal',
    domain: ['#01579b', '#7aa3e5', '#a8385d', '#00bfa5']
  };
  
  // Prompt list
  prompts: PromptTrackingData[] = [];  // All data from API (last 2 years)
  filteredPrompts: PromptTrackingData[] = [];  // Filtered for table display
  allPromptsData: PromptTrackingData[] = [];  // Store all data for local filtering
  monthlyData: any[] = [];  // Store monthly_data from backend for chart
  loading = false;
  initializing = false;
  error: string | null = null;
  
  // Task management
  currentTaskId: string | null = null;
  taskStatus: PromptTrackingTaskStatus | null = null;
  private statusPollingInterval: any = null;
  private wsSubscription: Subscription | undefined;
  progressPercentage = 0;
  currentStep: string | null = null;
  
  // Filters
  dateFilter: 'monthly' | 'quarterly' | 'semi-annual' | 'annual' | 'custom' = 'monthly';
  customStartDate: Date | null = null;
  customEndDate: Date | null = null;
  sortBy: 'relevance' | 'date' | 'mentions' | 'volume' = 'relevance';
  
  // Date validation
  get isDateRangeValid(): boolean {
    if (!this.customStartDate || !this.customEndDate) {
      return false;
    }
    return this.customEndDate >= this.customStartDate;
  }
  
  // Table pagination
  currentPage = 1;
  pageSize = 10;
  totalResults = 0;
  
  private subscriptions: Subscription[] = [];
  private promptResearchService = inject(PromptResearchService);

  ngOnInit(): void {
    const projectData = this.templateCommonService.getCurrentProjectData();
    if (projectData?.id) {
      this.currentProjectId = projectData.id;
      
      // Check localStorage for existing task ID
      const storedTaskId = this.getStoredTaskId();
      if (storedTaskId) {
        console.log(`📋 Found stored task ID: ${storedTaskId}, checking status...`);
        this.currentTaskId = storedTaskId;
        this.initializing = true;
        this.loading = true;
        
        // Check task status first
        const statusSub = this.promptTrackingService.getPromptTrackingStatus(storedTaskId).subscribe({
          next: (status) => {
            if (status.status === 'completed') {
              // Task already completed, fetch result
              console.log(`✅ Stored task ${storedTaskId} already completed, fetching result...`);
              this.removeStoredTaskId();
              this.getPromptTrackingResult(storedTaskId);
            } else if (status.status === 'failed' || status.status === 'cancelled') {
              // Task failed or cancelled, clear and reload
              console.log(`⚠️ Stored task ${storedTaskId} is ${status.status}, clearing and reloading...`);
              this.removeStoredTaskId();
              this.currentTaskId = null;
              this.loadData();
            } else {
              // Task still running, resume polling
              console.log(`🔄 Resuming polling for task ${storedTaskId}...`);
              this.startStatusPolling(storedTaskId);
            }
          },
          error: (err) => {
            // Task not found or expired, clear and reload
            console.log(`⚠️ Stored task ${storedTaskId} not found, clearing and reloading...`);
            this.removeStoredTaskId();
            this.currentTaskId = null;
            this.loadData();
          }
        });
        this.subscriptions.push(statusSub);
      } else {
        // Load data - the backend will return existing data if available
        this.loadData();
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.stopStatusPolling();
  }

  /** Stop both polling interval and WebSocket subscription. */
  private stopStatusPolling(): void {
    if (this.statusPollingInterval) {
      clearInterval(this.statusPollingInterval);
      this.statusPollingInterval = null;
    }
    if (this.wsSubscription) {
      this.wsSubscription.unsubscribe();
      this.wsSubscription = undefined;
    }
  }

  /**
   * Get stored task ID from localStorage
   */
  private getStoredTaskId(): string | null {
    if (!this.currentProjectId) return null;
    try {
      const key = `prompt-tracking-taskid-${this.currentProjectId}`;
      return localStorage.getItem(key);
    } catch (e) {
      console.error('Error reading from localStorage:', e);
      return null;
    }
  }

  /**
   * Store task ID in localStorage
   */
  private storeTaskId(taskId: string): void {
    if (!this.currentProjectId) return;
    try {
      const key = `prompt-tracking-taskid-${this.currentProjectId}`;
      localStorage.setItem(key, taskId);
    } catch (e) {
      console.error('Error writing to localStorage:', e);
    }
  }

  /**
   * Remove task ID from localStorage
   */
  private removeStoredTaskId(): void {
    if (!this.currentProjectId) return;
    try {
      const key = `prompt-tracking-taskid-${this.currentProjectId}`;
      localStorage.removeItem(key);
    } catch (e) {
      console.error('Error removing from localStorage:', e);
    }
  }

  /**
   * Cancel running task
   */
  cancelTask(): void {
    if (!this.currentTaskId) return;
    
    const sub = this.promptTrackingService.cancelPromptTrackingTask(this.currentTaskId).subscribe({
      next: (status) => {
        console.log(`🚫 Task ${this.currentTaskId} cancelled`);
        
        this.stopStatusPolling();
        
        // Remove from localStorage
        this.removeStoredTaskId();
        
        // Update UI
        this.currentTaskId = null;
        this.taskStatus = null;
        this.loading = false;
        this.initializing = false;
        this.progressPercentage = 0;
        this.currentStep = null;
        
        // Show message
        this.snackBar.open('Task cancelled successfully', 'Close', {
          duration: 3000,
          horizontalPosition: 'end',
          verticalPosition: 'top'
        });
        
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error cancelling task:', err);
        const errorMessage = err.error?.detail || 'Failed to cancel task';
        this.snackBar.open(`Error: ${errorMessage}`, 'Close', {
          duration: 5000,
          horizontalPosition: 'end',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
      }
    });
    
    this.subscriptions.push(sub);
  }

  loadData(): void {
    const projectId = this.currentProjectId;
    if (!projectId) return;
    
    this.loading = true;
    this.error = null;
    
    // Check if we already have tracking data (without starting analysis)
    const sub = this.promptTrackingService.getTrackingData(projectId).subscribe({
      next: (r) => {
        if (r.data?.length > 0) {
          // Have data: load full dashboard
          this.promptTrackingService.getDashboardData(projectId).subscribe({
            next: (d) => {
              this.processDashboardResponse(d);
              this.loading = false;
              this.cdr.detectChanges();
            },
            error: (err) => {
              this.error = err.error?.detail || 'Failed to load dashboard';
              this.loading = false;
              this.cdr.detectChanges();
            }
          });
        } else {
          // No data: show empty state (user opens dialog via "Get started")
          this.loading = false;
          this.cdr.detectChanges();
        }
      },
      error: (err) => {
        this.error = err.error?.detail || 'Failed to load tracking data';
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
    this.subscriptions.push(sub);
  }

  refreshData(): void {
    if (!this.currentProjectId) return;
    
    // Show loading state
    this.loading = true;
    this.initializing = true;
    this.error = null;
    this.progressPercentage = 0;
    this.currentStep = null;
    
    // Clear existing table data immediately
    this.prompts = [];
    this.filteredPrompts = [];
    this.allPromptsData = [];
    this.monthlyData = [];
    this.chartData = [];
    this.totalResults = 0;
    this.currentPage = 1;
    this.cdr.detectChanges();
    
    // Call refresh API (now task-based)
    const sub = this.promptTrackingService.refreshTrackingData(this.currentProjectId).subscribe({
      next: (response) => {
        // New API always returns task_id
        if (response.task_id) {
          this.currentTaskId = response.task_id;
          
          // Store task ID in localStorage
          this.storeTaskId(response.task_id);
          
          // If status is "completed", fetch the result immediately
          if (response.status === 'completed') {
            console.log(`✅ Refresh task ${response.task_id} already completed, fetching result...`);
            this.removeStoredTaskId(); // Remove from localStorage since it's done
            this.getPromptTrackingResultForRefresh(response.task_id, 0);
          } else {
            // Task is pending or in progress, start polling
            console.log(`🔄 Started refresh task: ${response.task_id}`);
            
            // Show initial message
            this.snackBar.open(
              `Refresh started. Processing new data...`,
              'Close',
              {
                duration: 3000,
                horizontalPosition: 'end',
                verticalPosition: 'top'
              }
            );
            
            // Start polling with refresh-specific handler
            this.startRefreshStatusPolling(response.task_id, 0);
          }
        } else {
          // Should not happen with new API, but handle gracefully
          console.error('No task_id in refresh response:', response);
          this.error = 'Invalid response from server';
          this.loading = false;
          this.initializing = false;
          this.cdr.detectChanges();
        }
      },
      error: (err) => {
        console.error('Error starting refresh:', err);
        const errorMessage = err.error?.detail || err.error?.message || 'Failed to start refresh';
        this.error = errorMessage;
        this.loading = false;
        this.initializing = false;
        this.snackBar.open(`Refresh failed: ${errorMessage}`, 'Close', {
          duration: 5000,
          horizontalPosition: 'end',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    });
    this.subscriptions.push(sub);
  }

  /** Open "How do you want to get prompts?" dialog; on confirm, start analysis with chosen source. */
  openPromptTrackingSourceDialog(): void {
    const projectId = this.currentProjectId;
    if (!projectId) return;
    const projectData = this.templateCommonService.getCurrentProjectData();
    const projectDetails = projectData
      ? {
          projectName: projectData.name,
          brand:
            (projectData as any)?.brands?.[0] ??
            (projectData as any)?.selectedEntities?.[0]?.name ??
            projectData.name,
          category:
            projectData.category ??
            (projectData as any)?.categories?.find((c: { isPrimary?: boolean }) => c.isPrimary)?.name,
          industry: (projectData as any)?.industry ?? projectData.industry,
          country: (projectData as any)?.primary_country ?? (projectData as any)?.country,
        }
      : undefined;
    const dialogData: PromptTrackingSourceDialogData = { projectId, projectDetails };
    const ref = this.dialog.open(PromptTrackingSourceDialogComponent, {
      width: '80%',
      maxWidth: '90vw',
      maxHeight: '90vh',
      data: dialogData,
      disableClose: true,
      hasBackdrop: true,
    });
    ref.afterClosed().subscribe((result: PromptTrackingSourceResult | null) => {
      if (!result) return;
      const body =
        result.source === 'upload' && result.prompts?.length
          ? { prompts: result.prompts }
          : result.source === 'select' && result.research_id
            ? { research_id: result.research_id }
            : undefined;
      this.startPromptTrackingAnalysis(body);
    });
  }

  /**
   * Poll refresh task status (or subscribe to WebSocket when useWebSocket).
   * Polling code kept for rollback – do not remove.
   */
  private startRefreshStatusPolling(taskId: string, deletedCount: number): void {
    if (this.taskProgressWs.useWebSocket && this.currentProjectId) {
      this.stopStatusPolling();
      this.taskProgressWs.connect();
      this.wsSubscription = this.taskProgressWs
        .messagesByPath(this.currentProjectId)
        .pipe(filter((msg) => (msg.data as any)?.task_id === taskId))
        .subscribe((msg) => {
          const status = msg.data as unknown as PromptTrackingTaskStatus;
          if (!status) return;
          this.taskStatus = status;
          this.progressPercentage = status.progress_percentage || 0;
          this.currentStep = status.current_step || (status.status === 'pending' ? 'Initializing refresh...' : 'Refreshing data...');
          if (status.status === 'completed') {
            this.stopStatusPolling();
            this.removeStoredTaskId();
            this.getPromptTrackingResultForRefresh(taskId, deletedCount);
          } else if (status.status === 'failed' || status.status === 'cancelled') {
            this.stopStatusPolling();
            this.removeStoredTaskId();
            this.error = status.status === 'cancelled' ? 'Refresh was cancelled' : (status.error_message || 'Refresh failed');
            this.loading = false;
            this.initializing = false;
            this.snackBar.open(this.error, 'Close', {
              duration: 5000,
              horizontalPosition: 'end',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            });
            this.cdr.detectChanges();
          }
          this.cdr.detectChanges();
        });
      this.promptTrackingService.getPromptTrackingStatus(taskId).subscribe({
        next: (status) => {
          this.taskStatus = status;
          this.progressPercentage = status.progress_percentage || 0;
          this.currentStep = status.current_step || (status.status === 'pending' ? 'Initializing refresh...' : 'Refreshing data...');
          if (status.status === 'completed') {
            this.stopStatusPolling();
            this.removeStoredTaskId();
            this.getPromptTrackingResultForRefresh(taskId, deletedCount);
          } else if (status.status === 'failed' || status.status === 'cancelled') {
            this.stopStatusPolling();
            this.removeStoredTaskId();
            this.error = status.status === 'cancelled' ? 'Refresh was cancelled' : (status.error_message || 'Refresh failed');
            this.loading = false;
            this.initializing = false;
            this.snackBar.open(`Refresh failed: ${this.error}`, 'Close', {
              duration: 5000,
              horizontalPosition: 'end',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            });
            this.cdr.detectChanges();
          }
          this.cdr.detectChanges();
        },
        error: () => {}
      });
      return;
    }

    // Fallback: polling
    let pollCount = 0;
    const initialInterval = 2000;
    const regularInterval = 5000;
    const pollStatus = () => {
      this.promptTrackingService.getPromptTrackingStatus(taskId).subscribe({
        next: (status: PromptTrackingTaskStatus) => {
          pollCount++;
          this.taskStatus = status;
          this.progressPercentage = status.progress_percentage || 0;
          this.currentStep = status.current_step || (status.status === 'pending' ? 'Initializing refresh...' : 'Refreshing data...');
          if (status.status === 'completed') {
            if (this.statusPollingInterval) {
              clearInterval(this.statusPollingInterval);
              this.statusPollingInterval = null;
            }
            this.removeStoredTaskId();
            this.getPromptTrackingResultForRefresh(taskId, deletedCount);
          } else if (status.status === 'failed' || status.status === 'cancelled') {
            if (this.statusPollingInterval) {
              clearInterval(this.statusPollingInterval);
              this.statusPollingInterval = null;
            }
            this.removeStoredTaskId();
            this.error = status.status === 'cancelled' ? 'Refresh was cancelled' : (status.error_message || 'Refresh failed');
            this.loading = false;
            this.initializing = false;
            this.snackBar.open(this.error, 'Close', {
              duration: 5000,
              horizontalPosition: 'end',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            });
            this.cdr.detectChanges();
          } else {
            if (pollCount === 3 && this.statusPollingInterval) {
              clearInterval(this.statusPollingInterval);
              this.statusPollingInterval = setInterval(pollStatus, regularInterval);
            }
          }
          this.cdr.detectChanges();
        },
        error: (error: any) => {
          console.error('Error polling refresh status:', error);
          if (this.statusPollingInterval) {
            clearInterval(this.statusPollingInterval);
            this.statusPollingInterval = null;
          }
          this.error = 'Failed to get refresh status';
          this.loading = false;
          this.initializing = false;
          this.cdr.detectChanges();
        }
      });
    };
    this.statusPollingInterval = setInterval(pollStatus, initialInterval);
    pollStatus();
  }

  /**
   * Get refresh result and show success message
   */
  private getPromptTrackingResultForRefresh(taskId: string, deletedCount: number): void {
    // Get task status to extract deleted_count and new_prompts_count from result
    const sub = this.promptTrackingService.getPromptTrackingStatus(taskId).subscribe({
      next: (status) => {
        const result = status.result || {};
        const actualDeletedCount = result.deleted_count || deletedCount || 0;
        const newPromptsCount = result.new_prompts_count || 0;
        
        // After refresh completes, reload data using the regular endpoint
        // The refresh task result doesn't contain dashboard data, just metadata
        // So we reload the data which will fetch the fresh data
        this.loadData();
        
        // Show success message
        this.snackBar.open(
          `Data refreshed successfully! Deleted ${actualDeletedCount} old records and created ${newPromptsCount} new records.`,
          'Close',
          {
            duration: 5000,
            horizontalPosition: 'end',
            verticalPosition: 'top'
          }
        );
        
        this.loading = false;
        this.initializing = false;
        this.currentTaskId = null;
        this.taskStatus = null;
        // Ensure localStorage is cleared
        this.removeStoredTaskId();
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error getting refresh result:', err);
        this.error = err.error?.detail || 'Failed to get refresh result';
        this.loading = false;
        this.initializing = false;
        // Clear localStorage on error
        this.removeStoredTaskId();
        this.cdr.detectChanges();
      }
    });
    
    this.subscriptions.push(sub);
  }

  /**
   * Start prompt tracking analysis (task-based).
   * body: optional { prompts } from upload or { research_id } from Prompt Creation.
   */
  startPromptTrackingAnalysis(body?: { prompts?: string[]; research_id?: string }): void {
    if (!this.currentProjectId) return;
    
    this.loading = true;
    this.initializing = false;
    this.error = null;
    this.progressPercentage = 0;
    this.currentStep = null;
    
    const sub = this.promptTrackingService.startPromptTrackingAnalysis(this.currentProjectId, body).subscribe({
      next: (response) => {
        // New API always returns task_id
        if (response.task_id) {
          this.currentTaskId = response.task_id;
          
          // Store task ID in localStorage
          this.storeTaskId(response.task_id);
          
          // If status is "completed", fetch the result immediately
          if (response.status === 'completed') {
            console.log(`✅ Task ${response.task_id} already completed, fetching result...`);
            this.removeStoredTaskId(); // Remove from localStorage since it's done
            this.getPromptTrackingResult(response.task_id);
          } else {
            // Task is pending or in progress, start polling
            this.initializing = true;
            console.log(`📝 Started prompt tracking task: ${response.task_id}, status: ${response.status}`);
            this.startStatusPolling(response.task_id);
          }
        } else {
          // Should not happen with new API, but handle gracefully
          console.error('No task_id in response:', response);
          this.error = 'Invalid response from server';
          this.loading = false;
          this.initializing = false;
          this.cdr.detectChanges();
        }
      },
      error: (err) => {
        console.error('Error starting prompt tracking analysis:', err);
        this.error = err.error?.detail || 'Failed to start prompt tracking analysis';
        this.loading = false;
        this.initializing = false;
        this.cdr.detectChanges();
      }
    });
    
    this.subscriptions.push(sub);
  }

  /**
   * Poll task status (or subscribe to WebSocket when useWebSocket).
   * Polling code kept for rollback – do not remove.
   */
  private startStatusPolling(taskId: string): void {
    if (this.taskProgressWs.useWebSocket && this.currentProjectId) {
      this.stopStatusPolling();
      this.taskProgressWs.connect();
      this.wsSubscription = this.taskProgressWs
        .messagesByPath(this.currentProjectId)
        .pipe(filter((msg) => (msg.data as any)?.task_id === taskId))
        .subscribe((msg) => {
          const status = msg.data as unknown as PromptTrackingTaskStatus;
          if (!status) return;
          this.applyStatusUpdate(status);
          if (status.status === 'completed') {
            this.stopStatusPolling();
            this.removeStoredTaskId();
            this.getPromptTrackingResult(taskId);
          } else if (status.status === 'failed' || status.status === 'cancelled') {
            this.stopStatusPolling();
            this.removeStoredTaskId();
            this.error = status.status === 'cancelled' ? 'Task was cancelled' : (status.error_message || 'Prompt tracking failed');
            this.loading = false;
            this.initializing = false;
            this.cdr.detectChanges();
          }
          this.cdr.detectChanges();
        });
      // Initial fetch via REST
      this.promptTrackingService.getPromptTrackingStatus(taskId).subscribe({
        next: (status) => {
          this.applyStatusUpdate(status);
          if (status.status === 'completed') {
            this.stopStatusPolling();
            this.removeStoredTaskId();
            this.getPromptTrackingResult(taskId);
          } else if (status.status === 'failed' || status.status === 'cancelled') {
            this.stopStatusPolling();
            this.removeStoredTaskId();
            this.error = status.status === 'cancelled' ? 'Task was cancelled' : (status.error_message || 'Prompt tracking failed');
            this.loading = false;
            this.initializing = false;
          }
          this.cdr.detectChanges();
        },
        error: () => {}
      });
      return;
    }

    // Fallback: polling
    let pollCount = 0;
    const initialInterval = 2000;
    const regularInterval = 5000;
    const pollStatus = () => {
      this.promptTrackingService.getPromptTrackingStatus(taskId).subscribe({
        next: (status: PromptTrackingTaskStatus) => {
          pollCount++;
          this.taskStatus = status;
          this.progressPercentage = status.progress_percentage || 0;
          this.currentStep = status.current_step || (status.status === 'pending' ? 'Initializing...' : 'Processing...');
          if (status.status === 'completed') {
            if (this.statusPollingInterval) {
              clearInterval(this.statusPollingInterval);
              this.statusPollingInterval = null;
            }
            this.removeStoredTaskId();
            this.getPromptTrackingResult(taskId);
          } else if (status.status === 'failed' || status.status === 'cancelled') {
            if (this.statusPollingInterval) {
              clearInterval(this.statusPollingInterval);
              this.statusPollingInterval = null;
            }
            this.removeStoredTaskId();
            this.error = status.status === 'cancelled' ? 'Task was cancelled' : (status.error_message || 'Prompt tracking failed');
            this.loading = false;
            this.initializing = false;
            this.cdr.detectChanges();
          } else {
            if (pollCount === 3 && this.statusPollingInterval) {
              clearInterval(this.statusPollingInterval);
              this.statusPollingInterval = setInterval(pollStatus, regularInterval);
            }
          }
          this.cdr.detectChanges();
        },
        error: (error: any) => {
          console.error('Error polling status:', error);
          if (this.statusPollingInterval) {
            clearInterval(this.statusPollingInterval);
            this.statusPollingInterval = null;
          }
          this.error = 'Failed to get task status';
          this.loading = false;
          this.initializing = false;
          this.cdr.detectChanges();
        }
      });
    };
    this.statusPollingInterval = setInterval(pollStatus, initialInterval);
    pollStatus();
  }

  private applyStatusUpdate(status: PromptTrackingTaskStatus): void {
    this.taskStatus = status;
    this.progressPercentage = status.progress_percentage || 0;
    this.currentStep = status.current_step || (status.status === 'pending' ? 'Initializing...' : 'Processing...');
  }

  /** Display label for prompt_source (ai_generated | uploaded | prompt_creation). */
  getPromptSourceLabel(source: string | undefined): string {
    if (!source) return '';
    const labels: Record<string, string> = {
      ai_generated: 'AI generated',
      uploaded: 'Uploaded',
      prompt_creation: 'Select from Prompt Creation',
    };
    return labels[source] || source;
  }

  /**
   * Get prompt tracking result when task is completed
   */
  private getPromptTrackingResult(taskId: string): void {
    const sub = this.promptTrackingService.getPromptTrackingResult(taskId).subscribe({
      next: (response) => {
        this.processDashboardResponse(response);
        this.loading = false;
        this.initializing = false;
        this.currentTaskId = null;
        this.taskStatus = null;
        // Ensure localStorage is cleared
        this.removeStoredTaskId();
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error getting prompt tracking result:', err);
        this.error = err.error?.detail || 'Failed to get prompt tracking result';
        this.loading = false;
        this.initializing = false;
        this.cdr.detectChanges();
      }
    });
    
    this.subscriptions.push(sub);
  }

  /**
   * Process dashboard response and update UI
   */
  private processDashboardResponse(response: any): void {
    // Set composite score
    this.compositeScore = response.composite_score;
    
    // Store all data for local filtering
    this.allPromptsData = response.tracking_data.data;
    
    // Apply local filter to populate prompts array (needed for table display)
    this.applyLocalFilter();
    
    // Store monthly_data for chart updates (e.g., when tabs change)
    this.monthlyData = response.monthly_data || [];
    
    // Use monthly_data from backend if available, otherwise prepare from raw data
    if (response.monthly_data && response.monthly_data.length > 0) {
      console.log('📊 Received monthly_data from backend:', response.monthly_data.length, 'months');
      // Backend provides month-wise grouped data
      this.prepareChartDataFromMonthly(response.monthly_data);
    } else {
      console.warn('⚠️ No monthly_data in response, using fallback');
      // Fallback: prepare chart from raw data
      this.prepareChartData();
    }
    
    this.applyFilters();
    
    // Force table refresh by resetting pagination and triggering change detection
    this.currentPage = 1;
    this.totalResults = this.filteredPrompts.length;
    
    // Force change detection to update table
    this.cdr.markForCheck();
    this.cdr.detectChanges();
    
    console.log(`✅ Table updated: ${this.filteredPrompts.length} prompts loaded, ${this.totalResults} total results`);
  }

  /**
   * @deprecated Use startPromptTrackingAnalysis instead
   * Kept for backward compatibility
   */
  loadDashboardData(): void {
    if (!this.currentProjectId) return;
    
    this.loading = true;
    this.error = null;
    
    // Get all data (last 2 years) - no filter parameters
    // Dashboard endpoint auto-triggers initial analysis if no data exists
    const sub = this.promptTrackingService.getDashboardData(this.currentProjectId).subscribe({
      next: (response) => {
        this.processDashboardResponse(response);
        this.loading = false;
        this.initializing = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading dashboard data:', err);
        this.error = err.error?.detail || 'Failed to load dashboard data';
        this.loading = false;
        this.initializing = false;
        this.cdr.detectChanges();
      }
    });
    
    this.subscriptions.push(sub);
  }

  applyLocalFilter(): void {
    // Filter data locally based on dateFilter without API call
    const now = new Date();
    let filteredData = [...this.allPromptsData];
    
    switch (this.dateFilter) {
      case 'monthly':
        // Show last 12 months
        const twelveMonthsAgo = new Date(now);
        twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
        filteredData = this.allPromptsData.filter(p => {
          const promptDate = new Date(p.collection_timestamp);
          return promptDate >= twelveMonthsAgo;
        });
        break;
        
      case 'quarterly':
        // Show last year (4 quarters)
        const oneYearAgo = new Date(now);
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
        filteredData = this.allPromptsData.filter(p => {
          const promptDate = new Date(p.collection_timestamp);
          return promptDate >= oneYearAgo;
        });
        break;
        
      case 'semi-annual':
        // Show last 2-3 years (use 2 years)
        const twoYearsAgo = new Date(now);
        twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
        filteredData = this.allPromptsData.filter(p => {
          const promptDate = new Date(p.collection_timestamp);
          return promptDate >= twoYearsAgo;
        });
        break;
        
      case 'annual':
        // Show last 2 years
        const twoYearsAgoAnnual = new Date(now);
        twoYearsAgoAnnual.setFullYear(twoYearsAgoAnnual.getFullYear() - 2);
        filteredData = this.allPromptsData.filter(p => {
          const promptDate = new Date(p.collection_timestamp);
          return promptDate >= twoYearsAgoAnnual;
        });
        break;
        
      case 'custom':
        // Filter by custom date range
        if (this.customStartDate && this.customEndDate && this.isDateRangeValid) {
          const start = new Date(this.customStartDate);
          start.setHours(0, 0, 0, 0); // Start of day
          const end = new Date(this.customEndDate);
          end.setHours(23, 59, 59, 999); // End of day
          filteredData = this.allPromptsData.filter(p => {
            const promptDate = new Date(p.collection_timestamp);
            return promptDate >= start && promptDate <= end;
          });
        } else {
          // If dates are not set yet, show all data (user hasn't selected dates yet)
          // This prevents empty data when custom option is first selected
          filteredData = [...this.allPromptsData];
        }
        break;
        
      default:
        // Default: show all data (last 12 months for monthly)
        const defaultTwelveMonthsAgo = new Date(now);
        defaultTwelveMonthsAgo.setMonth(defaultTwelveMonthsAgo.getMonth() - 12);
        filteredData = this.allPromptsData.filter(p => {
          const promptDate = new Date(p.collection_timestamp);
          return promptDate >= defaultTwelveMonthsAgo;
        });
    }
    
    // Deduplicate: Keep only the latest record per unique prompt_text
    const promptMap = new Map<string, PromptTrackingData>();
    
    for (const prompt of filteredData) {
      const promptText = prompt.prompt_text.toLowerCase().trim();
      const existing = promptMap.get(promptText);
      
      if (!existing) {
        // First occurrence of this prompt
        promptMap.set(promptText, prompt);
      } else {
        // Compare timestamps to keep the latest one
        const existingTime = new Date(existing.collection_timestamp || existing.updated_at).getTime();
        const currentTime = new Date(prompt.collection_timestamp || prompt.updated_at).getTime();
        
        if (currentTime > existingTime) {
          // Current record is newer, replace it
          promptMap.set(promptText, prompt);
        }
      }
    }
    
    // Convert map back to array (only latest records), sort by mentions (desc), then cap to 15 branded prompts
    filteredData = Array.from(promptMap.values());
    filteredData.sort((a, b) => (b.mentions ?? 0) - (a.mentions ?? 0));
    if (filteredData.length > MAX_BRANDED_PROMPTS_DISPLAY) {
      filteredData = filteredData.slice(0, MAX_BRANDED_PROMPTS_DISPLAY);
    }
    
    this.prompts = filteredData;
    this.totalResults = filteredData.length;
    
    console.log(`📊 Deduplicated prompts: ${this.allPromptsData.length} total records → ${this.prompts.length} unique branded prompts (max ${MAX_BRANDED_PROMPTS_DISPLAY})`);
  }

  // Keep these methods for backward compatibility if needed elsewhere
  loadCompositeScore(): void {
    if (!this.currentProjectId) return;
    
    const sub = this.promptTrackingService.getCompositeScore(this.currentProjectId).subscribe({
      next: (score) => {
        // Only set composite score if we have data or if score is meaningful
        // Score of 0 with all metrics 0 means no data exists yet
        if (score.score === 0 && 
            score.metrics.mentions === 0 && 
            score.metrics.citations === 0 && 
            score.metrics.impressions === 0 && 
            score.metrics.ai_share_of_voice === 0) {
          // No data yet - this is expected if initial analysis just completed
          // or if no prompts had brand mentions
          console.log('Composite score: No tracking data available yet');
        }
        this.compositeScore = score;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading composite score:', err);
        // Don't show error to user - composite score is optional
      }
    });
    
    this.subscriptions.push(sub);
  }

  loadTrackingData(): void {
    if (!this.currentProjectId) return;
    
    this.loading = true;
    this.error = null;
    
    const filterPeriod = this.dateFilter !== 'custom' ? this.dateFilter : undefined;
    const startDate = this.dateFilter === 'custom' && this.customStartDate 
      ? this.formatDateForAPI(this.customStartDate) 
      : undefined;
    const endDate = this.dateFilter === 'custom' && this.customEndDate 
      ? this.formatDateForAPI(this.customEndDate) 
      : undefined;
    
    const sub = this.promptTrackingService.getTrackingData(
      this.currentProjectId,
      filterPeriod,
      startDate,
      endDate
    ).subscribe({
      next: (response) => {
        this.prompts = response.data;
        this.totalResults = response.result_count;
        this.applyFilters();
        this.prepareChartData();
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading tracking data:', err);
        this.error = err.error?.detail || 'Failed to load prompt tracking data';
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
    
    this.subscriptions.push(sub);
  }

  performInitialAnalysis(): void {
    if (!this.currentProjectId) return;
    
    // Dashboard endpoint now auto-triggers initial analysis if no data exists
    // So we just reload the dashboard data
    this.initializing = true;
    this.error = null;
    
    this.loadData();
    
    // Reset initializing flag after a short delay to allow dashboard to complete
    setTimeout(() => {
      this.initializing = false;
      this.cdr.detectChanges();
    }, 2000);
  }

  prepareChartDataFromMonthly(monthlyData: any[]): void {
    // Use backend monthly_data and format based on selected filter
    const groupedData: { [key: string]: any } = {};
    
    // Filter monthly data by custom date range if custom filter is selected and dates are set
    let filteredMonthlyData = monthlyData;
    if (this.dateFilter === 'custom' && this.customStartDate && this.customEndDate && this.isDateRangeValid) {
      const start = new Date(this.customStartDate);
      start.setDate(1); // First day of the month
      start.setHours(0, 0, 0, 0);
      const end = new Date(this.customEndDate);
      // Get last day of the month
      end.setMonth(end.getMonth() + 1);
      end.setDate(0);
      end.setHours(23, 59, 59, 999);
      
      filteredMonthlyData = monthlyData.filter(monthData => {
        const monthDate = new Date(monthData.month + '-01'); // Parse YYYY-MM format
        // Check if month falls within the custom date range
        return monthDate >= start && monthDate <= end;
      });
    }
    // If custom is selected but dates are not set yet, show all monthly data
    
    filteredMonthlyData.forEach(monthData => {
      const monthDate = new Date(monthData.month + '-01'); // Parse YYYY-MM format
      let periodKey = '';
      
      switch (this.dateFilter) {
        case 'monthly':
          periodKey = this.formatMonthYear(monthDate);
          break;
        case 'quarterly':
          periodKey = this.formatQuarter(monthDate);
          break;
        case 'semi-annual':
          periodKey = this.formatSemiAnnual(monthDate);
          break;
        case 'annual':
          periodKey = monthDate.getFullYear().toString();
          break;
        case 'custom':
          // For custom range, use monthly format but only include months in range
          periodKey = this.formatMonthYear(monthDate);
          break;
        default:
          periodKey = this.formatMonthYear(monthDate);
          break;
      }
      
      if (!groupedData[periodKey]) {
        groupedData[periodKey] = {
          mentions: 0,
          citations: 0,
          impressions: 0,
          ai_share_of_voice: 0,
          volume: 0,
          count: 0
        };
      }
      
      // Sum values from monthly data
      groupedData[periodKey].mentions += monthData.mentions;
      groupedData[periodKey].citations += monthData.citations;
      groupedData[periodKey].impressions += monthData.impressions;
      groupedData[periodKey].ai_share_of_voice += monthData.ai_share_of_voice;
      groupedData[periodKey].volume += monthData.volume;
      groupedData[periodKey].count += monthData.prompt_count;
    });
    
    // Sort periods and prepare chart data
    let sortedPeriods = this.sortPeriods(Object.keys(groupedData));
    sortedPeriods = this.limitPeriods(sortedPeriods);
    
    this.chartData = sortedPeriods.map(period => {
      const data = groupedData[period];
      return {
        name: period,
        value: this.getMetricValue(data, this.selectedMetricTab)
      };
    });
    
    this.lineChart = [];
  }

  prepareChartData(): void {
    // Group data based on selected filter period
    let groupedData: { [key: string]: any } = {};
    
    this.prompts.forEach(prompt => {
      let periodKey = '';
      const promptDate = new Date(prompt.collection_timestamp);
      
      switch (this.dateFilter) {
        case 'monthly':
          // Format: "Jan-2024", "Feb-2024", etc.
          periodKey = this.formatMonthYear(promptDate);
          break;
          
        case 'quarterly':
          // Format: "Q1-2024", "Q2-2024", etc.
          periodKey = this.formatQuarter(promptDate);
          break;
          
        case 'semi-annual':
          // Format: "H1-2024", "H2-2024", etc.
          periodKey = this.formatSemiAnnual(promptDate);
          break;
          
        case 'annual':
          // Format: "2024", "2025", etc.
          periodKey = promptDate.getFullYear().toString();
          break;
          
        case 'custom':
        default:
          // Default to monthly format
          periodKey = this.formatMonthYear(promptDate);
          break;
      }
      
      if (!groupedData[periodKey]) {
        groupedData[periodKey] = {
          mentions: 0,
          citations: 0,
          impressions: 0,
          ai_share_of_voice: 0,
          count: 0
        };
      }
      
      // Sum values (not average)
      groupedData[periodKey].mentions += prompt.mentions;
      groupedData[periodKey].citations += prompt.citations;
      groupedData[periodKey].impressions += prompt.impressions;
      groupedData[periodKey].ai_share_of_voice += prompt.ai_share_of_voice;
      groupedData[periodKey].count += 1;
    });
    
    // Sort periods and prepare chart data
    let sortedPeriods = this.sortPeriods(Object.keys(groupedData));
    
    // Limit periods based on filter
    sortedPeriods = this.limitPeriods(sortedPeriods);
    
    this.chartData = sortedPeriods.map(period => {
      const data = groupedData[period];
      return {
        name: period,
        value: this.getMetricValue(data, this.selectedMetricTab)
      };
    });
    
    // Prepare line chart for comparison (if multiple brands)
    this.lineChart = [];
  }

  formatMonthYear(date: Date): string {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]}-${date.getFullYear()}`;
  }

  formatQuarter(date: Date): string {
    const quarter = Math.floor(date.getMonth() / 3) + 1;
    return `Q${quarter}-${date.getFullYear()}`;
  }

  formatSemiAnnual(date: Date): string {
    const half = date.getMonth() < 6 ? 1 : 2;
    return `H${half}-${date.getFullYear()}`;
  }

  sortPeriods(periods: string[]): string[] {
    // Custom sort based on filter type
    if (this.dateFilter === 'annual') {
      // Sort years numerically
      return periods.sort((a, b) => parseInt(a) - parseInt(b));
    } else {
      // Sort by date (extract year and period number)
      return periods.sort((a, b) => {
        // Extract year and period from strings like "Jan-2024", "Q1-2024", "H1-2024"
        const aMatch = a.match(/(\d{4})/);
        const bMatch = b.match(/(\d{4})/);
        const aYear = aMatch ? parseInt(aMatch[1]) : 0;
        const bYear = bMatch ? parseInt(bMatch[1]) : 0;
        
        if (aYear !== bYear) {
          return aYear - bYear;
        }
        
        // For same year, sort by period
        if (a.includes('Q')) {
          const aQ = parseInt(a.match(/Q(\d)/)?.[1] || '0');
          const bQ = parseInt(b.match(/Q(\d)/)?.[1] || '0');
          return aQ - bQ;
        } else if (a.includes('H')) {
          const aH = parseInt(a.match(/H(\d)/)?.[1] || '0');
          const bH = parseInt(b.match(/H(\d)/)?.[1] || '0');
          return aH - bH;
        } else {
          // Monthly: compare month names
          const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const aMonth = months.indexOf(a.substring(0, 3));
          const bMonth = months.indexOf(b.substring(0, 3));
          return aMonth - bMonth;
        }
      });
    }
  }

  limitPeriods(periods: string[]): string[] {
    // Limit periods based on filter type
    switch (this.dateFilter) {
      case 'monthly':
        // Show last 12 months
        return periods.slice(-12);
        
      case 'quarterly':
        // Show last 4 quarters (1 year)
        return periods.slice(-4);
        
      case 'semi-annual':
        // Show last 4 semi-annual periods (2 years)
        return periods.slice(-4);
        
      case 'annual':
        // Show last 2 years
        return periods.slice(-2);
        
      case 'custom':
      default:
        // Show all periods for custom/default
        return periods;
    }
  }

  getMetricValue(data: any, metric: string): number {
    // Return sum, not average
    switch (metric) {
      case 'mentions':
        return data.mentions;
      case 'citations':
        return data.citations;
      case 'impressions':
        return data.impressions;
      case 'ai_share_of_voice':
        // For AI Share of Voice, calculate average percentage
        return data.count > 0 ? Math.round(data.ai_share_of_voice / data.count) : 0;
      default:
        return 0;
    }
  }

  getMonthFromDate(dateString: string): string {
    const date = new Date(dateString);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  }

  onMetricTabChange(event: any): void {
    const tabIndex = event.index;
    const tabs = ['mentions', 'citations', 'impressions', 'ai_share_of_voice'];
    this.selectedMetricTab = tabs[tabIndex] as any;
    
    // Use monthly_data if available, otherwise fallback to prepareChartData
    if (this.monthlyData && this.monthlyData.length > 0) {
      this.prepareChartDataFromMonthly(this.monthlyData);
    } else {
      this.prepareChartData();
    }
    
    this.cdr.detectChanges();
  }

  getXAxisLabel(): string {
    switch (this.dateFilter) {
      case 'monthly':
        return 'Month';
      case 'quarterly':
        return 'Quarter';
      case 'semi-annual':
        return 'Semi-Annual Period';
      case 'annual':
        return 'Year';
      case 'custom':
        return 'Period';
      default:
        return 'Month';
    }
  }

  getMetricLabel(metric: string): string {
    switch (metric) {
      case 'mentions':
        return 'Brand Mentions';
      case 'citations':
        return 'Citations';
      case 'impressions':
        return 'Impressions';
      case 'ai_share_of_voice':
        return 'AI Share of Voice';
      default:
        return metric;
    }
  }

  applyFilters(): void {
    let filtered = [...this.prompts];
    
    // Apply sorting
    filtered.sort((a, b) => {
      switch (this.sortBy) {
        case 'date':
          return new Date(b.collection_timestamp).getTime() - new Date(a.collection_timestamp).getTime();
        case 'mentions':
          return b.mentions - a.mentions;
        case 'volume':
          return (b.volume || 0) - (a.volume || 0);
        case 'relevance':
        default:
          // Sort by mentions first, then by date
          if (b.mentions !== a.mentions) {
            return b.mentions - a.mentions;
          }
          return new Date(b.collection_timestamp).getTime() - new Date(a.collection_timestamp).getTime();
      }
    });
    
    this.filteredPrompts = filtered;
    this.currentPage = 1;
  }

  onDateFilterChange(): void {
    // If custom is selected but dates are not set, don't filter yet
    // This prevents showing empty data when custom option is first selected
    if (this.dateFilter === 'custom' && (!this.customStartDate || !this.customEndDate)) {
      // Show all data until user selects dates
      this.prompts = [...this.allPromptsData];
      this.totalResults = this.allPromptsData.length;
      
      // Prepare chart with all monthly data
      if (this.monthlyData && this.monthlyData.length > 0) {
        this.prepareChartDataFromMonthly(this.monthlyData);
      } else {
        this.prepareChartData();
      }
      
      this.applyFilters();
      this.cdr.detectChanges();
      return;
    }
    
    // Filter locally without API call
    this.applyLocalFilter();
    this.applyFilters();
    
    // Use monthly_data if available, otherwise fallback to prepareChartData
    if (this.monthlyData && this.monthlyData.length > 0) {
      this.prepareChartDataFromMonthly(this.monthlyData);
    } else {
      this.prepareChartData();
    }
    
    this.cdr.detectChanges();
  }

  onSortChange(): void {
    this.applyFilters();
    this.cdr.detectChanges();
  }

  openPromptDetail(prompt: PromptTrackingData): void {
    const dialogRef = this.dialog.open(PromptDetailDialogComponent, {
      width: '80%',
      maxWidth: '95vw',
      maxHeight: '90vh',
      disableClose: true,
      panelClass: 'prompt-detail-dialog-panel',
      data: {
        promptId: prompt.id,
        projectId: this.currentProjectId
      },
    });
  }

  exportData(): void {
    if (!this.currentProjectId) return;
    
    const filterPeriod = this.dateFilter !== 'custom' ? this.dateFilter : undefined;
    // Convert Date objects to ISO string format (YYYY-MM-DD)
    const startDate = this.dateFilter === 'custom' && this.customStartDate 
      ? this.formatDateForAPI(this.customStartDate) 
      : undefined;
    const endDate = this.dateFilter === 'custom' && this.customEndDate 
      ? this.formatDateForAPI(this.customEndDate) 
      : undefined;
    
    const sub = this.promptTrackingService.exportTrackingData(
      this.currentProjectId,
      filterPeriod,
      startDate,
      endDate
    ).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `prompt_tracking_${this.currentProjectId}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.snackBar.open('Data exported successfully', 'Close', { duration: 2000 });
      },
      error: (err) => {
        console.error('Error exporting data:', err);
        this.snackBar.open('Failed to export data', 'Close', { duration: 3000 });
      }
    });
    
    this.subscriptions.push(sub);
  }

  getPaginatedPrompts(): PromptTrackingData[] {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    return this.filteredPrompts.slice(start, end);
  }

  getTotalPages(): number {
    return Math.ceil(this.filteredPrompts.length / this.pageSize);
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  }

  getTimeAgo(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  }

  formatDateForAPI(date: Date): string {
    // Format date as YYYY-MM-DD for API
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatEstimatedTime(seconds: number): string {
    if (seconds < 60) {
      return `${seconds} second${seconds !== 1 ? 's' : ''}`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      if (minutes > 0) {
        return `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`;
      }
      return `${hours} hour${hours !== 1 ? 's' : ''}`;
    }
  }

  // Tab navigation methods
  getVisibleTabs(): any[] {
    if (this.parentTabs && typeof (this.parentTabs as any).getVisibleTabs === 'function') {
      return (this.parentTabs as any).getVisibleTabs();
    }
    // Fallback: return common tabs
    return [
      { id: 'overview', name: 'Overview', icon: 'dashboard' },
      { id: 'ai-citations', name: 'AI Citations', icon: 'link' },
      { id: 'category-analysis', name: 'Category Analysis', icon: 'category' },
      { id: 'consumer-insights', name: 'Consumer Insights', icon: 'psychology' },
      { id: 'sentiment-tracking', name: 'Sentiment Tracking', icon: 'sentiment_satisfied' },
      { id: 'ai-content-index', name: 'AI Content Index', icon: 'article' },
      { id: 'prompt-research', name: 'Prompt Creation', icon: 'search' },
      { id: 'content-creation', name: 'Content Creation', icon: 'create' },
      { id: 'prompt-tracking', name: 'Prompt Tracking', icon: 'track_changes' }
    ];
  }

  getSubTabs(parentTabId: string): any[] {
    if (this.parentTabs && typeof (this.parentTabs as any).getSubTabs === 'function') {
      return (this.parentTabs as any).getSubTabs(parentTabId);
    }
    return [];
  }

  openTab(tabId: string): void {
    if (this.parentTabs && typeof (this.parentTabs as any).openTab === 'function') {
      (this.parentTabs as any).openTab(tabId);
    }
  }

  openSubTab(parentTabId: string, subTabId: string): void {
    if (this.parentTabs && typeof (this.parentTabs as any).openSubTab === 'function') {
      (this.parentTabs as any).openSubTab(parentTabId, subTabId);
    }
  }
}
